import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  Skeleton,
  Pagination,
  Button,
  IconButton,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  Business as BusinessIcon,
  Star,
  PictureAsPdf,
  ShareOutlined,
  FilterList,
  ExpandMore,
  ExpandLess,
  MoreVert,
  Flag,
  School,
  WorkspacePremium,
  AccessTime,
  LocalOffer,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  BusinessCourse,
  CourseLevel,
  CourseLanguage,
  CourseType,
  CourseAccessType,
  BusinessCourseListParams,
} from '@/constants/types';
import { businessCourseService } from '@/services/business/businessCourseService';
import { getFileUrl } from '@/utils/businessHelpers';
import { slateJsonToText } from '@/utils/slateSerializer';
import { ShareCourseModal } from '@/components/business/ShareCourseModal';
import { ReportBusinessCourseDialog } from '@/components/business/ReportBusinessCourseDialog';

const LEVELS: (CourseLevel | 'all')[] = ['all', 'Beginner', 'Intermediate', 'Advanced'];
const LANGUAGES: (CourseLanguage | 'all')[] = ['all', 'English', 'Tamil', 'Bilingual'];
const COURSE_TYPES: (CourseType | 'all')[] = ['all', 'Free', 'Paid'];
const ACCESS_TYPES: (CourseAccessType | 'all')[] = ['all', 'Lifetime', 'Limited', 'Subscription'];

const PAGE_SIZE = 12;
const PRICE_MAX = 100000;

const CardSkeleton: React.FC = () => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
    <Skeleton variant="rectangular" height={180} />
    <CardContent>
      <Skeleton width="40%" height={20} sx={{ mb: 1 }} />
      <Skeleton width="80%" height={24} sx={{ mb: 0.5 }} />
      <Skeleton width="50%" height={28} sx={{ mb: 0.5 }} />
      <Skeleton width="100%" height={20} />
    </CardContent>
  </Card>
);

export const ExploreCoursesPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [courses, setCourses] = useState<BusinessCourse[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [level, setLevel] = useState<CourseLevel | 'all'>('all');
  const [language, setLanguage] = useState<CourseLanguage | 'all'>('all');
  const [courseType, setCourseType] = useState<CourseType | 'all'>('all');
  const [accessType, setAccessType] = useState<CourseAccessType | 'all'>('all');
  const [certificationOnly, setCertificationOnly] = useState(false);
  const [promotedOnly, setPromotedOnly] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, PRICE_MAX]);

  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ current: 1, pages: 1, total: 0, limit: PAGE_SIZE });

  const [shareCourse, setShareCourse] = useState<BusinessCourse | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement | null; course: BusinessCourse | null }>({ el: null, course: null });
  const [reportCourse, setReportCourse] = useState<BusinessCourse | null>(null);

  useEffect(() => {
    loadCourses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, level, language, courseType, accessType, certificationOnly, promotedOnly, priceRange, page]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, level, language, courseType, accessType, certificationOnly, promotedOnly, priceRange]);

  const loadCourses = async () => {
    try {
      setIsLoading(true);
      const params: BusinessCourseListParams = {
        page,
        limit: PAGE_SIZE,
        isActive: true,
      };

      if (searchQuery.trim()) params.q = searchQuery.trim();
      if (level !== 'all') params.level = level;
      if (language !== 'all') params.language = language;
      if (courseType !== 'all') params.courseType = courseType;
      if (accessType !== 'all') params.accessType = accessType;
      if (certificationOnly) params.certificationAvailable = true;
      if (promotedOnly) params.promoted = true;
      if (priceRange[0] > 0) params.minPrice = priceRange[0];
      if (priceRange[1] < PRICE_MAX) params.maxPrice = priceRange[1];

      const response = await businessCourseService.getBusinessCourses(params);
      setCourses(response.data.businessCourses);
      setPagination(response.data.pagination);
    } catch (err: any) {
      console.error('Failed to load courses:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewCourse = (course: BusinessCourse) => {
    const bizId = typeof course.businessId === 'string' ? course.businessId : course.businessId?._id;
    if (bizId) {
      navigate(`/businesses/${bizId}/courses/${course._id}`);
    } else {
      navigate(`/courses/${course._id}`);
    }
  };

  const getBusinessInfo = (course: BusinessCourse) => {
    if (typeof course.businessId === 'object' && course.businessId) return course.businessId;
    return null;
  };

  const clearFilters = () => {
    setSearchQuery('');
    setLevel('all');
    setLanguage('all');
    setCourseType('all');
    setAccessType('all');
    setCertificationOnly(false);
    setPromotedOnly(false);
    setPriceRange([0, PRICE_MAX]);
  };

  const totalPages = pagination ? pagination.pages || Math.ceil(pagination.total / PAGE_SIZE) : 1;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        Explore Courses
      </Typography>

      {/* Search & Filter toggle */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 10 }}>
            <TextField
              fullWidth
              placeholder="Search courses by name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterList />}
              endIcon={showFilters ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowFilters(!showFilters)}
              sx={{ textTransform: 'none' }}
            >
              Filters
            </Button>
          </Grid>
        </Grid>

        <Collapse in={showFilters}>
          <Box sx={{ mt: 2, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Grid container spacing={3}>
              {/* Level */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Level</Typography>
                <ToggleButtonGroup
                  value={level}
                  exclusive
                  onChange={(_, v) => v && setLevel(v)}
                  size="small"
                  fullWidth
                >
                  {LEVELS.map((l) => (
                    <ToggleButton key={l} value={l} sx={{ textTransform: 'none' }}>
                      {l === 'all' ? 'All' : l}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>

              {/* Course Type */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Course Type</Typography>
                <ToggleButtonGroup
                  value={courseType}
                  exclusive
                  onChange={(_, v) => v && setCourseType(v)}
                  size="small"
                  fullWidth
                >
                  {COURSE_TYPES.map((t) => (
                    <ToggleButton key={t} value={t} sx={{ textTransform: 'none' }}>
                      {t === 'all' ? 'All' : t}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>

              {/* Language */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Language</Typography>
                <ToggleButtonGroup
                  value={language}
                  exclusive
                  onChange={(_, v) => v && setLanguage(v)}
                  size="small"
                  fullWidth
                >
                  {LANGUAGES.map((l) => (
                    <ToggleButton key={l} value={l} sx={{ textTransform: 'none' }}>
                      {l === 'all' ? 'All' : l}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>

              {/* Access Type */}
              <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>Access</Typography>
                <ToggleButtonGroup
                  value={accessType}
                  exclusive
                  onChange={(_, v) => v && setAccessType(v)}
                  size="small"
                  fullWidth
                >
                  {ACCESS_TYPES.map((a) => (
                    <ToggleButton key={a} value={a} sx={{ textTransform: 'none' }}>
                      {a === 'all' ? 'All' : a}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>

              {/* Price Range — only meaningful for Paid */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Price Range: ₹{priceRange[0].toLocaleString('en-IN')} – ₹{priceRange[1].toLocaleString('en-IN')}
                </Typography>
                <Slider
                  value={priceRange}
                  onChange={(_, v) => setPriceRange(v as [number, number])}
                  valueLabelDisplay="auto"
                  min={0}
                  max={PRICE_MAX}
                  step={500}
                  valueLabelFormat={(v) => `₹${v.toLocaleString('en-IN')}`}
                  disabled={courseType === 'Free'}
                />
              </Grid>

              {/* Toggles + Clear */}
              <Grid size={{ xs: 12, md: 4 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={certificationOnly}
                        onChange={(e) => setCertificationOnly(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Certification only</Typography>}
                  />
                  {/* <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={promotedOnly}
                        onChange={(e) => setPromotedOnly(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Promoted only</Typography>}
                  /> */}
                  <Button onClick={clearFilters} variant="text" size="small" sx={{ textTransform: 'none', alignSelf: 'flex-start', mt: 0.5 }}>
                    Clear filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Box>

      {/* Results count */}
      {!isLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {pagination.total} {pagination.total === 1 ? 'course' : 'courses'} found
        </Typography>
      )}

      {/* Cards */}
      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
              <CardSkeleton />
            </Grid>
          ))}
        </Grid>
      ) : courses.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 10,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 3,
          }}
        >
          <School sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No courses found
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Try adjusting your filters
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {courses.map((course) => {
            const businessInfo = getBusinessInfo(course);
            const bizId = typeof course.businessId === 'string' ? course.businessId : course.businessId?._id;
            const isPaid = course.courseType === 'Paid';
            const hasDiscount = isPaid && (course.discount || 0) > 0 && (course.price || 0) > 0;
            const finalPrice =
              course.finalPrice ??
              (hasDiscount
                ? (course.price as number) - ((course.price as number) * (course.discount as number)) / 100
                : course.price ?? 0);

            return (
              <Grid key={course._id} size={{ xs: 12, sm: 6, md: 4, lg: 4 }}>
                <Card
                  elevation={0}
                  onClick={() => handleViewCourse(course)}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      boxShadow: 2,
                    },
                  }}
                >
                  {(course.isCurrentlyPromoted || course.isPromoted) && (
                    <Chip
                      icon={<Star sx={{ fontSize: 14 }} />}
                      label="Promoted"
                      size="small"
                      color="warning"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 1,
                        height: 22,
                        fontSize: '0.7rem',
                      }}
                    />
                  )}

                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAnchor({ el: e.currentTarget, course });
                    }}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 1,
                      bgcolor: 'rgba(255,255,255,0.9)',
                      color: 'text.primary',
                      boxShadow: 1,
                      '&:hover': { bgcolor: 'white' },
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>

                  {course.courseThumbnail?.url ? (
                    <CardMedia
                      component="img"
                      height={180}
                      image={course.courseThumbnail.url}
                      alt={course.courseTitle}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 180,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                      }}
                    >
                      <School sx={{ fontSize: 56, color: 'text.disabled' }} />
                    </Box>
                  )}

                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Chips */}
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={course.level}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                      <Chip
                        label={course.courseType}
                        size="small"
                        color={isPaid ? 'secondary' : 'success'}
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                      {course.certificationAvailable && (
                        <Chip
                          icon={<WorkspacePremium sx={{ fontSize: 12 }} />}
                          label="Certified"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>

                    {/* Title */}
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {course.courseTitle}
                    </Typography>

                    {/* Price */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, my: 0.5, flexWrap: 'wrap' }}>
                      {isPaid ? (
                        <>
                          <Typography variant="h6" color="primary" fontWeight={700}>
                            ₹{Number(finalPrice).toLocaleString('en-IN')}
                          </Typography>
                          {hasDiscount && (
                            <>
                              <Typography variant="caption" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                                ₹{(course.price as number).toLocaleString('en-IN')}
                              </Typography>
                              <Chip
                                icon={<LocalOffer sx={{ fontSize: 11 }} />}
                                label={`${course.discount}% off`}
                                size="small"
                                color="error"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            </>
                          )}
                        </>
                      ) : (
                        <Typography variant="h6" color="success.main" fontWeight={700}>
                          Free
                        </Typography>
                      )}
                    </Box>

                    {/* Meta */}
                    {(course.duration || course.numberOfModules) && (
                      <Box sx={{ display: 'flex', gap: 1.5, color: 'text.secondary', mb: 0.5 }}>
                        {course.duration && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTime sx={{ fontSize: 14 }} />
                            <Typography variant="caption">{course.duration}</Typography>
                          </Box>
                        )}
                        {course.numberOfModules ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <School sx={{ fontSize: 14 }} />
                            <Typography variant="caption">{course.numberOfModules} modules</Typography>
                          </Box>
                        ) : null}
                      </Box>
                    )}

                    {/* Description */}
                    {course.description && (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
                        {slateJsonToText(course.description)}
                      </Typography>
                    )}

                    {/* Business */}
                    {businessInfo && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          pt: 1,
                          borderTop: `1px solid ${theme.palette.divider}`,
                          cursor: 'pointer',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (bizId) navigate(`/businesses/${bizId}`);
                        }}
                      >
                        <Avatar
                          src={businessInfo.logoId ? getFileUrl(businessInfo.logoId) : undefined}
                          sx={{ width: 24, height: 24, fontSize: 12 }}
                        >
                          {businessInfo.businessName?.charAt(0) || <BusinessIcon sx={{ fontSize: 14 }} />}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1 }}>
                          {businessInfo.businessName}
                        </Typography>
                      </Box>
                    )}

                    {/* Actions — pinned bottom */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mt: 'auto',
                        pt: 1.5,
                      }}
                    >
                      {course.brochure?.url ? (
                        <Button
                          size="small"
                          startIcon={<PictureAsPdf />}
                          href={course.brochure.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          sx={{ textTransform: 'none' }}
                        >
                          Brochure
                        </Button>
                      ) : (
                        <Box />
                      )}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShareCourse(course);
                        }}
                        sx={{ border: '1px solid', borderColor: 'divider' }}
                      >
                        <ShareOutlined fontSize="small" />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}

      {/* Share Modal */}
      <ShareCourseModal
        open={!!shareCourse}
        onClose={() => setShareCourse(null)}
        course={shareCourse}
        businessId={
          shareCourse
            ? typeof shareCourse.businessId === 'string'
              ? shareCourse.businessId
              : shareCourse.businessId?._id || ''
            : ''
        }
      />

      {/* Card Kebab Menu */}
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={() => setMenuAnchor({ el: null, course: null })}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            const c = menuAnchor.course;
            setMenuAnchor({ el: null, course: null });
            if (c) setReportCourse(c);
          }}
        >
          <ListItemIcon>
            <Flag fontSize="small" />
          </ListItemIcon>
          <ListItemText>Report</ListItemText>
        </MenuItem>
      </Menu>

      {/* Report Dialog */}
      <ReportBusinessCourseDialog
        open={!!reportCourse}
        onClose={() => setReportCourse(null)}
        courseId={reportCourse?._id}
        courseTitle={reportCourse?.courseTitle}
      />
    </Box>
  );
};
